import { FormEvent, useEffect, useMemo, useState } from "react";
import { ApiError } from "../../api/client";
import {
  Student,
  StudentInput,
  createStudent,
  updateStudent,
} from "../../api/students";
import type { TrainingPeriodTree } from "../../api/trainingCohorts";
import { extractIdentityDigits, validateTurkishIdentityNo } from "../../utils/identity";
import {
  extractPhoneDigits,
  formatPhoneDisplay,
  formatPhoneInput,
  normalizePhoneForApi,
  validateTurkishPhone,
} from "../../utils/phone";
import "./StudentFormModal.css";

type Props = {
  student: Student | null;
  cohortTree: TrainingPeriodTree[];
  onClose: () => void;
  onSaved: (student: Student) => void;
};

const LICENSE_OPTIONS = ["", "B", "B1", "A1", "A2", "A", "C", "D", "E", "M", "F"];

function emptyInput(): StudentInput {
  return {
    fullName: "",
    phone: "",
    email: "",
    identityNo: "",
    licenseClass: "",
    examDate: "",
    notes: "",
    status: "active",
    trainingSubgroupId: null,
  };
}

function fromStudent(student: Student): StudentInput {
  return {
    fullName: student.full_name,
    phone: formatPhoneDisplay(student.phone),
    email: student.email || "",
    identityNo: student.identity_no || "",
    licenseClass: student.license_class || "",
    examDate: student.driving_exam_date
      ? student.driving_exam_date.slice(0, 10)
      : student.exam_date
        ? student.exam_date.slice(0, 10)
        : "",
    notes: student.notes || "",
    status: student.status,
    trainingSubgroupId: student.training_subgroup_id,
  };
}

function cleanForApi(input: StudentInput): StudentInput {
  const trim = (v: string | null | undefined) => (typeof v === "string" ? v.trim() : v);
  const optional = (v: string | null | undefined) => {
    const trimmed = trim(v);
    return trimmed && trimmed.length > 0 ? trimmed : null;
  };
  const phoneRaw = optional(input.phone);
  const identityRaw = optional(input.identityNo);
  return {
    fullName: (trim(input.fullName) || "") as string,
    phone: phoneRaw ? normalizePhoneForApi(phoneRaw) : null,
    email: optional(input.email),
    identityNo: identityRaw ? extractIdentityDigits(identityRaw) : null,
    licenseClass: optional(input.licenseClass),
    examDate: optional(input.examDate),
    notes: optional(input.notes),
    status: input.status,
    trainingSubgroupId: input.trainingSubgroupId ?? null,
  };
}

export function StudentFormModal({ student, cohortTree, onClose, onSaved }: Props) {
  const [form, setForm] = useState<StudentInput>(() =>
    student ? fromStudent(student) : emptyInput(),
  );
  const [periodId, setPeriodId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(student ? fromStudent(student) : emptyInput());
    setError(null);
    if (student?.training_cohort) {
      setPeriodId(student.training_cohort.period_id || "");
      setGroupId(student.training_cohort.group_id || "");
    } else {
      setPeriodId("");
      setGroupId("");
    }
  }, [student]);

  const isEdit = Boolean(student);

  const selectedPeriod = useMemo(
    () => cohortTree.find((p) => p.id === periodId),
    [cohortTree, periodId],
  );
  const selectedGroup = useMemo(
    () => selectedPeriod?.groups.find((g) => g.id === groupId),
    [selectedPeriod, groupId],
  );

  const update = <K extends keyof StudentInput>(key: K, value: StudentInput[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    const payload = cleanForApi(form);
    if (!payload.fullName) {
      setError("Ad soyad zorunludur.");
      return;
    }
    const phoneErr = validateTurkishPhone(extractPhoneDigits(form.phone || ""));
    if (phoneErr) {
      setError(phoneErr);
      return;
    }
    const identityErr = validateTurkishIdentityNo(extractIdentityDigits(form.identityNo || ""));
    if (identityErr) {
      setError(identityErr);
      return;
    }
    setSubmitting(true);
    try {
      const saved = isEdit
        ? await updateStudent(student!.id, payload)
        : await createStudent(payload);
      onSaved(saved);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "Kayit basarisiz.");
      } else {
        setError("Sunucuya ulasilamadi.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="kp-modal-backdrop" role="dialog" aria-modal="true">
      <div className="kp-modal kp-card kp-student-form">
        <div className="kp-modal__head">
          <div>
            <strong>{isEdit ? "Kursiyer Duzenle" : "Yeni Kursiyer"}</strong>
            <span>{isEdit ? student!.full_name : "Yeni kayit olusturun"}</span>
          </div>
          <button className="kp-btn kp-btn--ghost" type="button" onClick={onClose} disabled={submitting}>
            Kapat
          </button>
        </div>

        <form className="kp-student-form__grid" onSubmit={submit} autoComplete="off">
          <label className="kp-student-form__field kp-student-form__field--full">
            <span>Ad Soyad *</span>
            <input
              className="kp-input"
              type="text"
              required
              maxLength={150}
              value={form.fullName}
              onChange={(e) => update("fullName", e.target.value)}
            />
          </label>

          <fieldset className="kp-student-form__cohort kp-student-form__field--full">
            <legend>Kayit donemi / Grup / Alt grup (istege bagli)</legend>
            <div className="kp-student-form__cohort-grid">
              <label className="kp-student-form__field">
                <span>Donem</span>
                <select
                  className="kp-input"
                  value={periodId}
                  onChange={(e) => {
                    setPeriodId(e.target.value);
                    setGroupId("");
                    update("trainingSubgroupId", null);
                  }}
                >
                  <option value="">—</option>
                  {cohortTree.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="kp-student-form__field">
                <span>Grup</span>
                <select
                  className="kp-input"
                  value={groupId}
                  disabled={!periodId}
                  onChange={(e) => {
                    setGroupId(e.target.value);
                    update("trainingSubgroupId", null);
                  }}
                >
                  <option value="">—</option>
                  {selectedPeriod?.groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="kp-student-form__field">
                <span>Alt grup</span>
                <select
                  className="kp-input"
                  value={form.trainingSubgroupId || ""}
                  disabled={!groupId}
                  onChange={(e) =>
                    update("trainingSubgroupId", e.target.value ? e.target.value : null)
                  }
                >
                  <option value="">—</option>
                  {selectedGroup?.subgroups.map((sg) => (
                    <option key={sg.id} value={sg.id}>
                      {sg.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <small className="kp-form-status__hint">
              Kurumsal subelerden bagimsizdir. Degisiklik gecmis randevulari etkilemez.
            </small>
          </fieldset>

          <label className="kp-student-form__field">
            <span>Telefon</span>
            <input
              className="kp-input"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              maxLength={16}
              value={form.phone || ""}
              onChange={(e) => update("phone", formatPhoneInput(e.target.value))}
              onBlur={() => {
                const digits = extractPhoneDigits(form.phone || "");
                if (digits) update("phone", formatPhoneDisplay(digits));
              }}
              placeholder="0555 685 75 80"
            />
          </label>

          <label className="kp-student-form__field">
            <span>E-posta</span>
            <input
              className="kp-input"
              type="email"
              maxLength={150}
              value={form.email || ""}
              onChange={(e) => update("email", e.target.value)}
            />
          </label>

          <label className="kp-student-form__field">
            <span>TC Kimlik No</span>
            <input
              className="kp-input"
              type="text"
              inputMode="numeric"
              maxLength={11}
              value={form.identityNo || ""}
              onChange={(e) => update("identityNo", extractIdentityDigits(e.target.value))}
              placeholder="12345678901"
            />
          </label>

          <label className="kp-student-form__field">
            <span>Ehliyet Sinifi</span>
            <select
              className="kp-input"
              value={form.licenseClass || ""}
              onChange={(e) => update("licenseClass", e.target.value)}
            >
              {LICENSE_OPTIONS.map((opt) => (
                <option key={opt || "_empty"} value={opt}>
                  {opt || "-"}
                </option>
              ))}
            </select>
          </label>

          <label className="kp-student-form__field">
            <span>Direksiyon Sınav Tarihi</span>
            <input
              className="kp-input"
              type="date"
              value={form.examDate || ""}
              onChange={(e) => update("examDate", e.target.value)}
            />
            <small className="kp-form-status__hint">
              Eğitmen ve kursiyer ders planını bu tarihe göre yapar. Teorik sınav bilgisi PDF
              içe aktarma ile güncellenir.
            </small>
          </label>

          {isEdit && student?.theoretical_exam_date && (
            <div className="kp-student-form__field kp-student-form__field--readonly">
              <span>Teorik Sınav (PDF)</span>
              <p className="kp-student-form__readonly-value">
                {student.theoretical_exam_date.slice(0, 10)}
                {student.theoretical_exam_time
                  ? ` · ${student.theoretical_exam_time.slice(0, 5)}`
                  : ""}
                {student.theoretical_exam_location ? ` · ${student.theoretical_exam_location}` : ""}
              </p>
            </div>
          )}

          {isEdit && (
            <label className="kp-student-form__field kp-student-form__field--full">
              <span>Durum</span>
              <select
                className="kp-input"
                value={form.status || "active"}
                onChange={(e) => update("status", e.target.value as StudentInput["status"])}
              >
                <option value="active">Aktif</option>
                <option value="passive">Pasif</option>
                <option value="graduated">Mezun</option>
              </select>
              <small className="kp-form-status__hint">
                Pasif kursiyerler yeni randevu planlamada listelenmez; mevcut kayitlar korunur.
              </small>
            </label>
          )}

          <label className="kp-student-form__field kp-student-form__field--full">
            <span>Notlar</span>
            <textarea
              className="kp-input"
              rows={3}
              maxLength={2000}
              value={form.notes || ""}
              onChange={(e) => update("notes", e.target.value)}
            />
          </label>

          {error && <div className="kp-student-form__error">{error}</div>}

          <div className="kp-student-form__actions">
            <button type="button" className="kp-btn" onClick={onClose} disabled={submitting}>
              Iptal
            </button>
            <button type="submit" className="kp-btn kp-btn--primary" disabled={submitting}>
              {submitting ? "Kaydediliyor…" : isEdit ? "Guncelle" : "Olustur"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
